import { IQuizResult, IQuizResultFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useEffect } from 'react';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

interface IStore {
  page: number;
  limit: number;
  sort: Record<string, 'asc' | 'desc'>;
  setPageAndLimit: (page: number, limit: number) => void;
  setSort: (sort: Record<string, 'asc' | 'desc'>) => void;
  quizResults: IQuizResult[];
  filters: IQuizResultFilters;
  setFilters: (filters: IQuizResultFilters) => void;
  setQuizResults: (quizResults: IQuizResult[]) => void;
}

const store = createStore<IStore>(set => ({
  sort: { createdAt: 'desc' },
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setSort: (sort: Record<string, 'asc' | 'desc'>) =>
    set(state => ({ ...state, sort, page: INITIAL_PAGE, limit: INITIAL_LIMIT, quizResults: [] })),
  quizResults: [],
  setQuizResults: (quizResults: IQuizResult[]) =>
    set(state => ({ ...state, quizResults: uniqBy(quizResults, '_id') })),
  filters: {},
  setFilters: (filters: IQuizResultFilters) =>
    set(state => ({ ...state, filters, page: INITIAL_PAGE, limit: INITIAL_LIMIT, quizResults: [] }))
}));

const QuizResultService = sdk.getService('QuizResultService');

export const useMyQuizResultList = () => {
  const {
    sort,
    setSort,
    page,
    limit,
    setPageAndLimit,
    quizResults,
    setQuizResults,
    filters,
    setFilters
  } = store();
  const queryKey = ['quiz-results', sort, page, limit, filters];

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () => QuizResultService.getQuizResults({ page, limit }, sort, filters)
  });

  useEffect(() => {
    if (data) {
      setQuizResults([...quizResults, ...data.docs]);
    }
  }, [data]);

  const fetchNextPage = () => setPageAndLimit(page + 1, limit);

  const pagination = {
    totalDocs: data?.totalDocs ?? 0,
    hasNextPage: data?.hasNextPage ?? false
  };

  return {
    isLoading,
    quizResults,
    pagination,
    sort,
    setSort,
    page,
    limit,
    setPageAndLimit,
    fetchNextPage,
    filters,
    setFilters
  };
};
